import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import adminApi from '../services/adminApi.js';
import api from '../services/api.js';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import NotificationManager from '../components/admin/NotificationManager.jsx';
import './AdminPage.css';

const AdminPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Determine active tab based on current route
  const getActiveTabFromPath = (pathname) => {
    if (pathname.includes('/admin/goals')) return 'goals';
    if (pathname.includes('/admin/reports')) return 'reports';
    if (pathname.includes('/admin/notifications')) return 'notifications';
    if (pathname.includes('/admin/settings')) return 'settings';
    if (pathname.includes('/admin/points')) return 'points';
    if (pathname.includes('/admin/esv')) return 'esv';
    return 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath(location.pathname));
  
  // Dashboard state
  const [goals, setGoals] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({});
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userSortBy, setUserSortBy] = useState('createdAt');
  const [userSortOrder, setUserSortOrder] = useState('DESC');
  const [currentUserPage, setCurrentUserPage] = useState(1);
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsPagination, setReportsPagination] = useState({});
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsStats, setReportsStats] = useState(null);
  const [currentReportsPage, setCurrentReportsPage] = useState(1);
  const [reportFilters, setReportFilters] = useState({
    startDate: '',
    endDate: '',
    readerId: '',
    familyId: ''
  });
  
  // ESV API state
  const [esvStats, setEsvStats] = useState(null);
  const [esvCacheStats, setEsvCacheStats] = useState(null);
  const [esvLoading, setEsvLoading] = useState(false);

  // User Points state
  const [pointsSummary, setPointsSummary] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [selectedUserForPoints, setSelectedUserForPoints] = useState(null);
  const [userPointsDetails, setUserPointsDetails] = useState(null);
  const [selectedReaderForHistory, setSelectedReaderForHistory] = useState(null);
  const [readerPointsHistory, setReaderPointsHistory] = useState(null);
  const [pointsHistoryPagination, setPointsHistoryPagination] = useState({});
  const [pointsFilters, setPointsFilters] = useState({
    reason: '',
    startDate: '',
    endDate: ''
  });
  const [currentPointsHistoryPage, setCurrentPointsHistoryPage] = useState(1);

  // Forms state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    description: '',
    targetValue: '',
    unit: '',
    isActive: true,
    displayOrder: 0
  });
  
  // Password reset state
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Debug logging
  console.log('AdminPage - User object:', user);
  console.log('AdminPage - User isAdmin:', user?.isAdmin);
  console.log('AdminPage - User authenticated:', !!user);

  useEffect(() => {
    if (user && user.isAdmin) {
      loadAdminData();
    }
  }, [user]);

  useEffect(() => {
    setActiveTab(getActiveTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab === 'users' && user?.isAdmin) {
      loadUsers();
    }
  }, [activeTab, currentUserPage, userSearch, userSortBy, userSortOrder]);

  useEffect(() => {
    if (activeTab === 'reports' && user?.isAdmin) {
      loadReports();
      loadReportsStats();
    }
  }, [activeTab, currentReportsPage, reportFilters]);

  useEffect(() => {
    if (activeTab === 'esv' && user?.isAdmin) {
      loadEsvStats();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'points' && user?.isAdmin) {
      loadPointsSummary();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedReaderForHistory && user?.isAdmin) {
      loadReaderPointsHistory();
    }
  }, [selectedReaderForHistory, currentPointsHistoryPage, pointsFilters]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [goalsRes, dashboardRes] = await Promise.all([
        adminApi.getGoals(),
        adminApi.getDashboard()
      ]);

      setGoals(goalsRes.data.goals || []);
      setDashboardStats(dashboardRes.data.dashboardStats);
      setError(null);
    } catch (err) {
      console.error('Failed to load admin data:', err);
      setError('Failed to load admin data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const params = {
        page: currentUserPage,
        limit: 20,
        search: userSearch,
        sortBy: userSortBy,
        sortOrder: userSortOrder
      };
      
      const response = await adminApi.getUsers(params);
      setUsers(response.data.users);
      setUsersPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setReportsLoading(true);
      const params = {
        page: currentReportsPage,
        limit: 50,
        ...reportFilters
      };
      
      const response = await adminApi.getReports(params);
      setReports(response.data.reports);
      setReportsPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to load reports:', err);
      setError('Failed to load reports. Please try again.');
    } finally {
      setReportsLoading(false);
    }
  };

  const loadReportsStats = async () => {
    try {
      const response = await adminApi.getReportsStats(reportFilters);
      setReportsStats(response.data);
    } catch (err) {
      console.error('Failed to load reports stats:', err);
    }
  };

  const loadEsvStats = async () => {
    try {
      setEsvLoading(true);
      const [usageRes, cacheRes] = await Promise.all([
        api.get('/esv/usage/stats'),
        api.get('/esv/cache/stats')
      ]);

      setEsvStats(usageRes.data.data);
      setEsvCacheStats(cacheRes.data.data);
    } catch (err) {
      console.error('Failed to load ESV stats:', err);
      
      let errorMessage = 'Failed to load ESV API statistics';
      if (err.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Admin privileges required to view ESV API statistics.';
      }
      
      setError(errorMessage);
    } finally {
      setEsvLoading(false);
    }
  };

  const loadPointsSummary = async () => {
    try {
      setPointsLoading(true);
      const response = await adminApi.getPointsSummary();
      setPointsSummary(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load points summary:', err);
      setError('Failed to load points summary. Please try again.');
    } finally {
      setPointsLoading(false);
    }
  };

  const loadUserDetails = async (userId) => {
    try {
      setPointsLoading(true);
      const response = await adminApi.getUserDetails(userId);
      setUserPointsDetails(response.data.user);
      setError(null);
    } catch (err) {
      console.error('Failed to load user details:', err);
      setError('Failed to load user details. Please try again.');
    } finally {
      setPointsLoading(false);
    }
  };

  const loadReaderPointsHistory = async () => {
    try {
      setPointsLoading(true);
      const params = {
        page: currentPointsHistoryPage,
        limit: 50,
        ...pointsFilters
      };
      
      const response = await adminApi.getReaderPointsHistory(selectedReaderForHistory.id, params);
      setReaderPointsHistory(response.data);
      setPointsHistoryPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      console.error('Failed to load reader points history:', err);
      setError('Failed to load points history. Please try again.');
    } finally {
      setPointsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const goalData = {
        ...formData,
        targetValue: parseInt(formData.targetValue),
        displayOrder: parseInt(formData.displayOrder)
      };

      if (editingGoal) {
        await adminApi.updateGoal(editingGoal.id, goalData);
      } else {
        await adminApi.createGoal(goalData);
      }

      setShowCreateForm(false);
      setEditingGoal(null);
      setFormData({
        key: '',
        name: '',
        description: '',
        targetValue: '',
        unit: '',
        isActive: true,
        displayOrder: 0
      });
      
      loadAdminData();
    } catch (err) {
      console.error('Failed to save goal:', err);
      setError('Failed to save goal. Please try again.');
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      key: goal.key,
      name: goal.name,
      description: goal.description || '',
      targetValue: goal.targetValue.toString(),
      unit: goal.unit || '',
      isActive: goal.isActive,
      displayOrder: goal.displayOrder
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal? This action cannot be undone.')) {
      return;
    }

    try {
      await adminApi.deleteGoal(goalId);
      loadAdminData();
    } catch (err) {
      console.error('Failed to delete goal:', err);
      setError('Failed to delete goal. Please try again.');
    }
  };

  const handleUpdateValues = async () => {
    try {
      await adminApi.updateGoalValues();
      loadAdminData();
    } catch (err) {
      console.error('Failed to update goal values:', err);
      setError('Failed to update goal values. Please try again.');
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingGoal(null);
    setFormData({
      key: '',
      name: '',
      description: '',
      targetValue: '',
      unit: '',
      isActive: true,
      displayOrder: 0
    });
  };

  // User management functions
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This will permanently delete all their data including reading reports.`)) {
      return;
    }

    try {
      await adminApi.deleteUser(userId);
      loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    try {
      await adminApi.resetUserPassword(passwordResetUser.id, newPassword);
      setPasswordResetUser(null);
      setNewPassword('');
      setError(null);
    } catch (err) {
      console.error('Failed to reset password:', err);
      setError('Failed to reset password. Please try again.');
    }
  };

  // Reports management functions
  const handleExportReports = async () => {
    try {
      const response = await adminApi.exportReports(reportFilters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reading-reports-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to export reports:', err);
      setError('Failed to export reports. Please try again.');
    }
  };

  const handleResetAllReports = async () => {
    const confirmation = prompt('Type "DELETE_ALL_REPORTS" to confirm you want to delete ALL reading reports. This action cannot be undone.');
    
    if (confirmation !== 'DELETE_ALL_REPORTS') {
      return;
    }

    try {
      await adminApi.resetAllReports();
      loadReports();
      loadReportsStats();
    } catch (err) {
      console.error('Failed to reset reports:', err);
      setError('Failed to reset reports. Please try again.');
    }
  };

  const handleCleanupFakeData = async () => {
    const warning = `⚠️ FAKE DATA CLEANUP WARNING ⚠️

This will permanently delete:
❌ All reading reports
❌ All points and transactions  
❌ All bible completion records
❌ Reset all site goal progress

This will preserve:
✅ User accounts
✅ Reader profiles
✅ Churches and families
✅ Reading plans
✅ Site goal definitions

Type "DELETE_ALL_FAKE_DATA" to confirm this cleanup.`;

    const confirmation = prompt(warning);
    
    if (confirmation !== 'DELETE_ALL_FAKE_DATA') {
      return;
    }

    try {
      setLoading(true);
      const response = await adminApi.cleanupFakeData();
      
      // Refresh all admin data
      loadAdminData();
      if (activeTab === 'reports') {
        loadReports();
        loadReportsStats();
      }
      
      alert(`✅ Cleanup completed successfully!\n\nDeleted:\n- ${response.data.deletedData.readingReports}\n- ${response.data.deletedData.pointsTransactions}\n- ${response.data.deletedData.readerPoints}\n- ${response.data.deletedData.bibleCompletions}\n\nPreserved:\n- ${response.data.preservedData.users}\n- ${response.data.preservedData.readers}`);
      
    } catch (err) {
      console.error('Failed to cleanup fake data:', err);
      setError('Failed to cleanup fake data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAllProgress = async () => {
    const confirmation = prompt('⚠️ DANGER: This will delete ALL reading reports and reset ALL points for ALL users.\n\nType "RESET_ALL_PROGRESS" to confirm this destructive action. This cannot be undone and is intended for site launch cleanup.');
    
    if (confirmation !== 'RESET_ALL_PROGRESS') {
      return;
    }

    const doubleConfirm = confirm('Are you absolutely sure? This will:\n- Delete all reading reports\n- Delete all points transactions\n- Reset all reader points to 0\n- Delete all Bible completion progress\n- Delete all reading plan progress\n- Delete all earned badges\n\nUsers and readers will remain, but all progress will be lost.');
    
    if (!doubleConfirm) {
      return;
    }

    try {
      setLoading(true);
      const response = await adminApi.resetAllProgress('RESET_ALL_PROGRESS');
      
      // Show success with summary
      alert(`✅ Reset completed successfully!\n\nSummary:\n- Reading Reports Deleted: ${response.data.summary.deletedCounts.readingReports}\n- Points Transactions Deleted: ${response.data.summary.deletedCounts.pointsTransactions}\n- Reader Points Reset: ${response.data.summary.resetCounts.readerPointsReset}\n- Bible Completions Deleted: ${response.data.summary.deletedCounts.bibleCompletions}\n- Plan Progress Deleted: ${response.data.summary.deletedCounts.planProgress}\n- Badges Earned Deleted: ${response.data.summary.deletedCounts.badgesEarned}`);
      
      // Reload all data
      loadReports();
      loadReportsStats();
      loadAdminData();
    } catch (err) {
      console.error('Failed to reset all progress:', err);
      setError('Failed to reset all progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  if (!user || !user.isAdmin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>You don't have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page loading">
        <LoadingSpinner size="large" message="Loading admin data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page error">
        <div className="error-message">
          <h2>Error Loading Admin Data</h2>
          <p>{error}</p>
          <button onClick={loadAdminData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">
          {activeTab === 'goals' && 'Site Goals'}
          {activeTab === 'reports' && 'Reading Reports'}
          {activeTab === 'notifications' && 'Notifications'}
          {activeTab === 'points' && 'User Points'}
          {activeTab === 'esv' && 'ESV API Monitor'}
          {activeTab === 'settings' && 'Admin Settings'}
          {activeTab === 'dashboard' && 'Admin Dashboard'}
        </h1>
        <p className="admin-page-description">
          {activeTab === 'goals' && 'Manage site-wide reading goals and targets'}
          {activeTab === 'reports' && 'View and manage all reading reports'}
          {activeTab === 'notifications' && 'Manage system notifications'}
          {activeTab === 'points' && 'View and manage user points and gamification'}
          {activeTab === 'esv' && 'Monitor ESV API usage and performance'}
          {activeTab === 'settings' && 'Configure admin settings and preferences'}
          {activeTab === 'dashboard' && 'Overview of your ReadTheWord application'}
        </p>
      </div>


      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboardStats && (
        <div className="dashboard-stats">
          <h2>Platform Overview</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{dashboardStats.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{dashboardStats.totalReaders}</div>
              <div className="stat-label">Active Readers</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{dashboardStats.totalReports}</div>
              <div className="stat-label">Reading Reports</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{dashboardStats.activeGoals}</div>
              <div className="stat-label">Active Goals</div>
            </div>
          </div>
        </div>
      )}

      {/* Goals Tab */}
      {activeTab === 'goals' && (
        <div className="goals-section">
          <div className="section-header">
            <h2>Site Goals</h2>
            <div className="header-actions">
              <button 
                onClick={handleUpdateValues}
                className="btn btn-secondary"
              >
                Update Goal Values
              </button>
              <button 
                onClick={() => setShowCreateForm(true)}
                className="btn btn-primary"
              >
                Add New Goal
              </button>
            </div>
          </div>

          {/* Create/Edit Goal Form */}
          {showCreateForm && (
            <div className="goal-form-container">
              <form onSubmit={handleSubmit} className="goal-form">
                <h3>{editingGoal ? 'Edit Goal' : 'Create New Goal'}</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="key">Key *</label>
                    <input
                      type="text"
                      id="key"
                      name="key"
                      value={formData.key}
                      onChange={handleInputChange}
                      required
                      disabled={!!editingGoal}
                      placeholder="e.g., total_chapters_read"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Total Chapters Read"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe what this goal represents..."
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="targetValue">Target Value *</label>
                    <input
                      type="number"
                      id="targetValue"
                      name="targetValue"
                      value={formData.targetValue}
                      onChange={handleInputChange}
                      required
                      min="0"
                      placeholder="e.g., 10000"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="unit">Unit</label>
                    <input
                      type="text"
                      id="unit"
                      name="unit"
                      value={formData.unit}
                      onChange={handleInputChange}
                      placeholder="e.g., chapters, readers"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="displayOrder">Display Order</label>
                    <input
                      type="number"
                      id="displayOrder"
                      name="displayOrder"
                      value={formData.displayOrder}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleCancel} className="btn btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingGoal ? 'Update Goal' : 'Create Goal'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Goals List */}
          <div className="goals-list">
            {goals.length === 0 ? (
              <div className="empty-state">
                <p>No goals have been created yet.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="btn btn-primary"
                >
                  Create Your First Goal
                </button>
              </div>
            ) : (
              goals.map(goal => (
                <div key={goal.id} className={`goal-card ${goal.isCompleted ? 'completed' : ''}`}>
                  <div className="goal-header">
                    <h3>{goal.name}</h3>
                    <div className="goal-status">
                      {goal.isCompleted && <span className="status-badge completed">Completed</span>}
                      {!goal.isActive && <span className="status-badge inactive">Inactive</span>}
                    </div>
                  </div>
                  
                  <div className="goal-details">
                    <div className="goal-key">
                      <strong>Key:</strong> {goal.key}
                    </div>
                    {goal.description && (
                      <div className="goal-description">
                        {goal.description}
                      </div>
                    )}
                  </div>

                  <div className="goal-progress">
                    <div className="progress-info">
                      <span className="current-value">{goal.currentValue.toLocaleString()}</span>
                      <span className="separator">/</span>
                      <span className="target-value">{goal.targetValue.toLocaleString()}</span>
                      {goal.unit && <span className="unit"> {goal.unit}</span>}
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${goal.progress}%` }}
                      />
                    </div>
                    <div className="progress-percentage">
                      {goal.progress}% complete
                    </div>
                  </div>

                  <div className="goal-actions">
                    <button 
                      onClick={() => handleEdit(goal)}
                      className="btn btn-small btn-secondary"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(goal.id)}
                      className="btn btn-small btn-danger"
                    >
                      Delete
                    </button>
                  </div>

                  {goal.lastUpdatedByUser && (
                    <div className="goal-meta">
                      <small>
                        Last updated by {goal.lastUpdatedByUser.name} on{' '}
                        {new Date(goal.updatedAt).toLocaleDateString()}
                      </small>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="users-section">
          <div className="section-header">
            <h2>User Management</h2>
            <div className="search-filters">
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="search-input"
              />
                             <select 
                 value={userSortBy} 
                 onChange={(e) => setUserSortBy(e.target.value)}
                 className="sort-select"
               >
                 <option value="createdAt">Date Created</option>
                 <option value="name">Name</option>
                 <option value="email">Email</option>
                 <option value="lastLogin">Last Login</option>
               </select>
              <button 
                onClick={() => setUserSortOrder(userSortOrder === 'ASC' ? 'DESC' : 'ASC')}
                className="btn btn-small btn-secondary"
              >
                {userSortOrder === 'ASC' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {usersLoading ? (
            <LoadingSpinner message="Loading users..." />
          ) : (
            <div className="users-list">
              {users.length === 0 ? (
                <div className="empty-state">
                  <p>No users found.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="users-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Readers</th>
                          <th>Reports</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(userItem => (
                          <tr key={userItem.id}>
                            <td>{userItem.name}</td>
                            <td>{userItem.email}</td>
                                                         <td>
                               <div className="user-status">
                                 {userItem.isAdmin && <span className="status-badge admin">Admin</span>}
                                 {userItem.emailVerified ? (
                                   <span className="status-badge verified">Verified</span>
                                 ) : (
                                   <span className="status-badge unverified">Unverified</span>
                                 )}
                               </div>
                             </td>
                            <td>{userItem.totalReaders}</td>
                            <td>{userItem.totalReports}</td>
                            <td>{new Date(userItem.createdAt).toLocaleDateString()}</td>
                            <td>
                              <div className="user-actions">
                                <button 
                                  onClick={() => setPasswordResetUser(userItem)}
                                  className="btn btn-small btn-secondary"
                                >
                                  Reset Password
                                </button>
                                <button 
                                  onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                                  className="btn btn-small btn-danger"
                                  disabled={userItem.id === user.id}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="users-card-container">
                    {users.map(userItem => (
                      <div key={userItem.id} className="user-card">
                        <div className="user-card-header">
                          <div>
                            <div className="user-card-name">{userItem.name}</div>
                            <div className="user-card-email">{userItem.email}</div>
                          </div>
                        </div>

                        <div className="user-card-status">
                          {userItem.isAdmin && <span className="status-badge admin">Admin</span>}
                          {userItem.emailVerified ? (
                            <span className="status-badge verified">Verified</span>
                          ) : (
                            <span className="status-badge unverified">Unverified</span>
                          )}
                        </div>

                        <div className="user-card-details">
                          <div className="user-card-detail">
                            <div className="user-card-detail-label">Readers</div>
                            <div className="user-card-detail-value">{userItem.totalReaders}</div>
                          </div>
                          <div className="user-card-detail">
                            <div className="user-card-detail-label">Reports</div>
                            <div className="user-card-detail-value">{userItem.totalReports}</div>
                          </div>
                          <div className="user-card-detail">
                            <div className="user-card-detail-label">Created</div>
                            <div className="user-card-detail-value">{new Date(userItem.createdAt).toLocaleDateString()}</div>
                          </div>
                        </div>

                        <div className="user-card-actions">
                          <button 
                            onClick={() => setPasswordResetUser(userItem)}
                            className="btn btn-small btn-secondary"
                          >
                            Reset Password
                          </button>
                          <button 
                            onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                            className="btn btn-small btn-danger"
                            disabled={userItem.id === user.id}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {usersPagination.totalPages > 1 && (
                    <div className="pagination">
                      <button 
                        onClick={() => setCurrentUserPage(prev => Math.max(1, prev - 1))}
                        disabled={!usersPagination.hasPrevPage}
                        className="btn btn-small"
                      >
                        Previous
                      </button>
                      <span>
                        Page {usersPagination.currentPage} of {usersPagination.totalPages}
                      </span>
                      <button 
                        onClick={() => setCurrentUserPage(prev => prev + 1)}
                        disabled={!usersPagination.hasNextPage}
                        className="btn btn-small"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="reports-section">
          <div className="section-header">
            <h2>Reading Reports Management</h2>
            <p>Starting a new year? Community and personal progress now default to the current calendar year automatically. Earlier years stay available in the Reading year selector. Manage seasonal dates and goals under <a href="/admin/challenges">Reading seasons</a>. No data deletion is needed.</p>
            <div className="header-actions">
              <button 
                onClick={handleExportReports}
                className="btn btn-secondary"
              >
                Export CSV
              </button>
              <button 
                onClick={handleResetAllReports}
                className="btn btn-danger"
              >
                Permanently delete reports
              </button>
              <button 
                onClick={handleResetAllProgress}
                className="btn btn-danger"
                style={{ marginLeft: '10px' }}
              >
                Permanently delete all progress
              </button>
              <button 
                onClick={handleCleanupFakeData}
                className="btn btn-danger"
                style={{ marginLeft: '10px', backgroundColor: '#dc2626' }}
              >
                🧹 Cleanup Fake Data
              </button>
            </div>
          </div>

          {/* Reports Statistics */}
          {reportsStats && (
            <div className="reports-stats">
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{reportsStats.totalReports}</div>
                  <div className="stat-label">Total Reports</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{reportsStats.uniqueReaders}</div>
                  <div className="stat-label">Unique Readers</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{reportsStats.uniqueChapters}</div>
                  <div className="stat-label">Unique Chapters</div>
                </div>
              </div>
            </div>
          )}

          {/* Reports Filters */}
          <div className="reports-filters">
            <div className="filter-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={reportFilters.startDate}
                  onChange={(e) => setReportFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={reportFilters.endDate}
                  onChange={(e) => setReportFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Reader ID</label>
                <input
                  type="text"
                  placeholder="Filter by reader ID"
                  value={reportFilters.readerId}
                  onChange={(e) => setReportFilters(prev => ({ ...prev, readerId: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Family ID</label>
                <input
                  type="text"
                  placeholder="Filter by family ID"
                  value={reportFilters.familyId}
                  onChange={(e) => setReportFilters(prev => ({ ...prev, familyId: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {reportsLoading ? (
            <LoadingSpinner message="Loading reports..." />
          ) : (
            <div className="reports-list">
              {reports.length === 0 ? (
                <div className="empty-state">
                  <p>No reports found.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="reports-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Reader</th>
                          <th>User</th>
                          <th>Book</th>
                          <th>Chapter</th>
                          <th>Date</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.map(report => (
                          <tr key={report.id}>
                            <td>{report.reader?.name || 'Unknown'}</td>
                            <td>{report.reader?.user?.email || 'Unknown'}</td>
                            <td>{report.chapter?.book || 'Unknown'}</td>
                            <td>{report.chapter?.chapter || 'Unknown'}</td>
                            <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                            <td>{report.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="reports-card-container">
                    {reports.map(report => (
                      <div key={report.id} className="report-card">
                        <div className="report-card-header">
                          <div>
                            <div className="report-card-reader">{report.reader?.name || 'Unknown'}</div>
                            <div className="user-card-email">{report.reader?.user?.email || 'Unknown'}</div>
                          </div>
                        </div>

                        <div className="report-card-details">
                          <div className="report-card-detail">
                            <div className="report-card-detail-label">Book</div>
                            <div className="report-card-detail-value">{report.chapter?.book || 'Unknown'}</div>
                          </div>
                          <div className="report-card-detail">
                            <div className="report-card-detail-label">Chapter</div>
                            <div className="report-card-detail-value">{report.chapter?.chapter || 'Unknown'}</div>
                          </div>
                          <div className="report-card-detail">
                            <div className="report-card-detail-label">Date</div>
                            <div className="report-card-detail-value">{new Date(report.createdAt).toLocaleDateString()}</div>
                          </div>
                        </div>

                        {report.notes && report.notes !== '-' && (
                          <div className={`report-card-notes ${!report.notes || report.notes === '-' ? 'no-notes' : ''}`}>
                            {report.notes || 'No notes'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {reportsPagination.totalPages > 1 && (
                    <div className="pagination">
                      <button 
                        onClick={() => setCurrentReportsPage(prev => Math.max(1, prev - 1))}
                        disabled={!reportsPagination.hasPrevPage}
                        className="btn btn-small"
                      >
                        Previous
                      </button>
                      <span>
                        Page {reportsPagination.currentPage} of {reportsPagination.totalPages}
                      </span>
                      <button 
                        onClick={() => setCurrentReportsPage(prev => prev + 1)}
                        disabled={!reportsPagination.hasNextPage}
                        className="btn btn-small"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="notifications-section">
          <NotificationManager />
        </div>
      )}

      {/* User Points Tab */}
      {activeTab === 'points' && (
        <div className="points-section">
          <div className="section-header">
            <h2>User Points Investigation</h2>
            <div className="header-actions">
              <button 
                onClick={loadPointsSummary}
                disabled={pointsLoading}
                className="btn btn-secondary"
              >
                {pointsLoading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>
          </div>

          {pointsLoading && <LoadingSpinner message="Loading points data..." />}

          {/* Points Summary Overview */}
          {pointsSummary && (
            <div className="points-summary">
              <h3>Points System Overview</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{pointsSummary.totalStats.totalTransactions}</div>
                  <div className="stat-label">Total Transactions</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{pointsSummary.totalStats.totalPointsAwarded}</div>
                  <div className="stat-label">Total Points Awarded</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{pointsSummary.totalStats.averagePointsPerTransaction}</div>
                  <div className="stat-label">Avg Points/Transaction</div>
                </div>
              </div>

              {/* Points by Reason */}
              <div className="points-breakdown">
                <h4>Points by Reason</h4>
                <div className="breakdown-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Reason</th>
                        <th>Count</th>
                        <th>Total Points</th>
                        <th>Avg Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointsSummary.pointsByReason.map(stat => (
                        <tr key={stat.reason}>
                          <td>
                            <span className={`reason-badge reason-${stat.reason}`}>
                              {stat.reason.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          </td>
                          <td>{stat.count}</td>
                          <td>{stat.totalPoints}</td>
                          <td>{stat.averagePoints}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top Readers */}
              <div className="top-readers">
                <h4>Top Readers by Points</h4>
                <div className="readers-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Reader</th>
                        <th>User Email</th>
                        <th>Total Points</th>
                        <th>Level</th>
                        <th>Lifetime Points</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointsSummary.topReaders.map(readerData => (
                        <tr key={readerData.reader.id}>
                          <td>{readerData.reader.name}</td>
                          <td>{readerData.reader.user?.email}</td>
                          <td>{readerData.points}</td>
                          <td>Level {readerData.level}</td>
                          <td>{readerData.lifetimePoints}</td>
                          <td>
                            <div className="reader-actions">
                              <button 
                                onClick={() => {
                                  setSelectedUserForPoints(readerData.reader.user);
                                  loadUserDetails(readerData.reader.user.id);
                                }}
                                className="btn btn-small btn-primary"
                              >
                                View User Details
                              </button>
                              <button 
                                onClick={() => {
                                  setSelectedReaderForHistory(readerData.reader);
                                  setCurrentPointsHistoryPage(1);
                                }}
                                className="btn btn-small btn-secondary"
                              >
                                View Points History
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="recent-transactions">
                <h4>Recent Points Transactions</h4>
                <div className="transactions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Reader</th>
                        <th>User</th>
                        <th>Points</th>
                        <th>Reason</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pointsSummary.recentTransactions.map(transaction => (
                        <tr key={transaction.id}>
                          <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                          <td>{transaction.reader?.name}</td>
                          <td>{transaction.reader?.user?.email}</td>
                          <td>
                            <span className={`points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                              {transaction.points > 0 ? '+' : ''}{transaction.points}
                            </span>
                          </td>
                          <td>
                            <span className={`reason-badge reason-${transaction.reason}`}>
                              {transaction.reason.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="description-cell">{transaction.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* User Details Modal */}
          {selectedUserForPoints && userPointsDetails && (
            <div className="modal-overlay">
              <div className="modal large-modal">
                <div className="modal-header">
                  <h3>User Details: {userPointsDetails.name}</h3>
                  <button 
                    onClick={() => {
                      setSelectedUserForPoints(null);
                      setUserPointsDetails(null);
                    }}
                    className="modal-close"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="user-info">
                    <div className="info-section">
                      <h4>User Information</h4>
                      <p><strong>Email:</strong> {userPointsDetails.email}</p>
                      <p><strong>Admin:</strong> {userPointsDetails.isAdmin ? 'Yes' : 'No'}</p>
                      <p><strong>Email Verified:</strong> {userPointsDetails.emailVerified ? 'Yes' : 'No'}</p>
                      <p><strong>Created:</strong> {new Date(userPointsDetails.createdAt).toLocaleDateString()}</p>
                      <p><strong>Last Login:</strong> {userPointsDetails.lastLogin ? new Date(userPointsDetails.lastLogin).toLocaleDateString() : 'Never'}</p>
                    </div>
                  </div>

                  <div className="readers-details">
                    <h4>Readers ({userPointsDetails.readers?.length || 0})</h4>
                    {userPointsDetails.readers?.map(reader => (
                      <div key={reader.id} className="reader-detail-card">
                        <div className="reader-header">
                          <h5>
                            {reader.name} 
                            {reader.isPrimary && <span className="primary-badge">Primary</span>}
                            {!reader.isActive && <span className="inactive-badge">Inactive</span>}
                          </h5>
                          <div className="reader-actions">
                            <button 
                              onClick={() => {
                                setSelectedReaderForHistory(reader);
                                setCurrentPointsHistoryPage(1);
                                setSelectedUserForPoints(null);
                                setUserPointsDetails(null);
                              }}
                              className="btn btn-small btn-primary"
                            >
                              View Points History
                            </button>
                          </div>
                        </div>
                        
                        {/* Points Information */}
                        {reader.points && (
                          <div className="points-info">
                            <div className="points-stats">
                              <div className="stat">
                                <span className="label">Total Points:</span>
                                <span className="value">{reader.points.totalPoints}</span>
                              </div>
                              <div className="stat">
                                <span className="label">Level:</span>
                                <span className="value">{reader.points.currentLevel}</span>
                              </div>
                              <div className="stat">
                                <span className="label">Lifetime Points:</span>
                                <span className="value">{reader.points.lifetimePoints}</span>
                              </div>
                              <div className="stat">
                                <span className="label">To Next Level:</span>
                                <span className="value">{reader.points.pointsToNextLevel}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Bible Completions */}
                        {reader.completions?.length > 0 && (
                          <div className="completions-info">
                            <h6>Bible Completions</h6>
                            {reader.completions.map(completion => (
                              <div key={completion.completionNumber} className="completion-item">
                                <span>Cycle {completion.completionNumber}: </span>
                                <span>{completion.chaptersRead}/1189 chapters</span>
                                <span>({completion.totalPoints} points)</span>
                                {completion.completedAt && (
                                  <span className="completed">✓ Completed {new Date(completion.completedAt).toLocaleDateString()}</span>
                                )}
                                {completion.isActive && <span className="active">Active</span>}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Recent Badges */}
                        {reader.earnedBadges?.length > 0 && (
                          <div className="badges-info">
                            <h6>Recent Badges ({reader.earnedBadges.length})</h6>
                            <div className="badges-list">
                              {reader.earnedBadges.slice(0, 5).map(badgeEarned => (
                                <div key={badgeEarned.earnedAt} className="badge-item">
                                  <span className={`badge-name ${badgeEarned.badge.rarity}`}>
                                    {badgeEarned.badge.name}
                                  </span>
                                  <span className="badge-points">+{badgeEarned.pointsAwarded} pts</span>
                                  <span className="badge-date">
                                    {new Date(badgeEarned.earnedAt).toLocaleDateString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recent Reading Activity */}
                        {reader.reports?.length > 0 && (
                          <div className="recent-activity">
                            <h6>Recent Reading Activity</h6>
                            <div className="activity-list">
                              {reader.reports.slice(0, 5).map(report => (
                                <div key={report.id} className="activity-item">
                                  <span>{report.chapter.book} {report.chapter.chapter}</span>
                                  <span>{new Date(report.readDate).toLocaleDateString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reader Points History Modal */}
          {selectedReaderForHistory && readerPointsHistory && (
            <div className="modal-overlay">
              <div className="modal large-modal">
                <div className="modal-header">
                  <h3>Points History: {readerPointsHistory.reader.name}</h3>
                  <button 
                    onClick={() => {
                      setSelectedReaderForHistory(null);
                      setReaderPointsHistory(null);
                    }}
                    className="modal-close"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="reader-info">
                    <p><strong>User:</strong> {readerPointsHistory.reader.user?.email}</p>
                  </div>

                  {/* Summary Statistics */}
                  {readerPointsHistory.summary && (
                    <div className="points-summary-section">
                      <h4>Points Summary by Reason</h4>
                      <div className="summary-grid">
                        {readerPointsHistory.summary.map(stat => (
                          <div key={stat.reason} className="summary-card">
                            <div className="summary-reason">{stat.reason.replace(/_/g, ' ').toUpperCase()}</div>
                            <div className="summary-stats">
                              <span>{stat.count} transactions</span>
                              <span>{stat.totalPoints} points</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div className="points-filters">
                    <div className="filter-row">
                      <div className="form-group">
                        <label>Reason</label>
                        <select
                          value={pointsFilters.reason}
                          onChange={(e) => setPointsFilters(prev => ({ ...prev, reason: e.target.value }))}
                        >
                          <option value="">All Reasons</option>
                          <option value="chapter_read_completion">Chapter Read (First Time)</option>
                          <option value="chapter_read_repeat">Chapter Read (Repeat)</option>
                          <option value="bible_completed">Bible Completed</option>
                          <option value="badge_earned">Badge Earned</option>
                          <option value="admin_adjustment">Admin Adjustment</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Start Date</label>
                        <input
                          type="date"
                          value={pointsFilters.startDate}
                          onChange={(e) => setPointsFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date</label>
                        <input
                          type="date"
                          value={pointsFilters.endDate}
                          onChange={(e) => setPointsFilters(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  <div className="transactions-history">
                    <h4>Transaction History</h4>
                    <div className="transactions-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Points</th>
                            <th>Reason</th>
                            <th>Description</th>
                            <th>Multiplier</th>
                            <th>Base Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {readerPointsHistory.transactions.map(transaction => (
                            <tr key={transaction.id}>
                              <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                              <td>
                                <span className={`points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                                  {transaction.points > 0 ? '+' : ''}{transaction.points}
                                </span>
                              </td>
                              <td>
                                <span className={`reason-badge reason-${transaction.reason}`}>
                                  {transaction.reason.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="description-cell">{transaction.description}</td>
                              <td>{transaction.multiplier}x</td>
                              <td>{transaction.basePoints}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {pointsHistoryPagination.totalPages > 1 && (
                      <div className="pagination">
                        <button 
                          onClick={() => setCurrentPointsHistoryPage(prev => Math.max(1, prev - 1))}
                          disabled={!pointsHistoryPagination.hasPrevPage}
                          className="btn btn-small"
                        >
                          Previous
                        </button>
                        <span>
                          Page {pointsHistoryPagination.currentPage} of {pointsHistoryPagination.totalPages}
                        </span>
                        <button 
                          onClick={() => setCurrentPointsHistoryPage(prev => prev + 1)}
                          disabled={!pointsHistoryPagination.hasNextPage}
                          className="btn btn-small"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ESV API Monitor Tab */}
      {activeTab === 'esv' && (
        <div className="esv-section">
          <div className="section-header">
            <h2>ESV API Monitoring</h2>
            <div className="header-actions">
              <button 
                onClick={loadEsvStats}
                disabled={esvLoading}
                className="btn-refresh"
              >
                {esvLoading ? 'Refreshing...' : 'Refresh Stats'}
              </button>
            </div>
          </div>

          {esvLoading && <LoadingSpinner />}

          {esvStats && (
            <div className="esv-stats">
              <div className="stats-overview">
                <h3>Usage Overview</h3>
                <div className="stats-grid">
                  <div className={`stat-card ${esvStats.today >= esvStats.warningThresholds.daily ? 'warning' : ''}`}>
                    <div className="stat-number">{esvStats.today}</div>
                    <div className="stat-label">Today</div>
                    <div className="stat-sublabel">
                      {Math.round((esvStats.today / esvStats.dailyLimit) * 100)}% of daily limit
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{esvStats.currentHour}</div>
                    <div className="stat-label">This Hour</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{esvStats.total}</div>
                    <div className="stat-label">Total Calls</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{esvStats.dailyLimit}</div>
                    <div className="stat-label">Daily Limit</div>
                  </div>
                </div>

                {esvStats.today >= esvStats.warningThresholds.daily && (
                  <div className="alert alert-warning">
                    ⚠️ Warning: You've used {Math.round((esvStats.today / esvStats.dailyLimit) * 100)}% of your daily ESV API limit!
                  </div>
                )}
              </div>

              <div className="usage-charts">
                <div className="chart-section">
                  <h3>Daily Usage (Last 7 Days)</h3>
                  <div className="chart-container">
                    {esvStats.dailyUsage.length > 0 ? (
                      <div className="bar-chart">
                        {esvStats.dailyUsage.map(([date, count]) => (
                          <div key={date} className="bar-item">
                            <div 
                              className={`bar ${count >= esvStats.warningThresholds.daily ? 'warning' : ''}`}
                              style={{ height: `${Math.min((count / esvStats.dailyLimit) * 100, 100)}%` }}
                              title={`${date}: ${count} calls`}
                            />
                            <div className="bar-label">
                              {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                            <div className="bar-value">{count}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No usage data available</p>
                    )}
                  </div>
                </div>

                <div className="chart-section">
                  <h3>Hourly Usage (Last 24 Hours)</h3>
                  <div className="chart-container">
                    {esvStats.hourlyUsage.length > 0 ? (
                      <div className="bar-chart hourly">
                        {esvStats.hourlyUsage.slice(0, 12).map(([hourKey, count]) => {
                          const [, hour] = hourKey.split('-');
                          return (
                            <div key={hourKey} className="bar-item">
                              <div 
                                className={`bar ${count >= esvStats.warningThresholds.hourly ? 'warning' : ''}`}
                                style={{ height: `${Math.min((count / 300) * 100, 100)}%` }}
                                title={`${hour}:00 - ${count} calls`}
                              />
                              <div className="bar-label">{hour}:00</div>
                              <div className="bar-value">{count}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p>No hourly data available</p>
                    )}
                  </div>
                </div>
              </div>

              {esvCacheStats && (
                <div className="cache-stats">
                  <h3>Cache Performance</h3>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-number">{esvCacheStats.size}</div>
                      <div className="stat-label">Cached Items</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">
                        {Math.round((esvCacheStats.totalSizeBytes || 0) / 1024)}KB
                      </div>
                      <div className="stat-label">Total Size</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{esvCacheStats.typeBreakdown?.text || 0}</div>
                      <div className="stat-label">Text Cached</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{esvCacheStats.typeBreakdown?.html || 0}</div>
                      <div className="stat-label">HTML Cached</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{esvCacheStats.typeBreakdown?.audio || 0}</div>
                      <div className="stat-label">Audio Cached</div>
                    </div>
                  </div>

                  {esvCacheStats.entries && esvCacheStats.entries.length > 0 && (
                    <div className="cache-entries">
                      <h4>Cached Content</h4>
                      <div className="table-container">
                        <table className="cache-table">
                          <thead>
                            <tr>
                              <th>Verse/Chapter</th>
                              <th>Type</th>
                              <th>Options</th>
                              <th>Age</th>
                              <th>Expires In</th>
                              <th>Size</th>
                            </tr>
                          </thead>
                          <tbody>
                            {esvCacheStats.entries.slice(0, 50).map((entry, index) => (
                              <tr key={index}>
                                <td className="reference-cell">
                                  <strong>{entry.reference}</strong>
                                </td>
                                <td>
                                  <span className={`type-badge type-${entry.type}`}>
                                    {entry.type.toUpperCase()}
                                  </span>
                                </td>
                                <td className="options-cell">
                                  {Object.keys(entry.options).length > 0 ? (
                                    <div className="options-list">
                                      {Object.entries(entry.options).map(([key, value]) => (
                                        <span key={key} className="option-tag">
                                          {key}: {value}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="no-options">Default</span>
                                  )}
                                </td>
                                <td className="time-cell">{entry.ageFormatted}</td>
                                <td className="time-cell">
                                  <span className={entry.expiresIn < 86400000 ? 'expires-soon' : ''}>
                                    {entry.expiresInFormatted}
                                  </span>
                                </td>
                                <td className="size-cell">
                                  {Math.round(entry.size / 1024)}KB
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {esvCacheStats.entries.length > 50 && (
                          <p className="table-note">
                            Showing first 50 of {esvCacheStats.entries.length} cached items
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Password Reset Modal */}
      {passwordResetUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reset Password for {passwordResetUser.name}</h3>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength="6"
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setPasswordResetUser(null);
                    setNewPassword('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
